<template>
  <section id="pricing" class="pricing-section py-5 bg-light">
    <div class="container">
      <h2 class="display-5 text-center text-dark fw-semibold mb-5 fade-in">{{ t('pricing.title') }}</h2>
      
      <div class="row g-4">
        <div class="col-lg-3 col-md-6 fade-in" v-for="(plan, index) in plans" :key="index">
          <div class="card pricing-card h-100" :class="plan.cardClass">
            <div class="card-body text-center p-4">
              <h5 class="card-title">{{ plan.title }}</h5>
              <h6 class="card-subtitle mb-3 text-muted">{{ plan.subtitle }}</h6>
              <div class="price-container my-4">
                <p class="display-6 fw-bold mb-0">{{ plan.price }}</p>
                <small v-if="plan.period" class="text-muted">{{ plan.currency }} /{{ plan.period }}</small>
              </div>
              <ul class="list-unstyled mb-4 text-start">
                <li v-for="(feature, featureIndex) in plan.features"
                    :key="featureIndex"
                    class="mb-2 d-flex align-items-start">
                  <i class="fas fa-check me-2 mt-1" :class="plan.checkColor"></i>
                  <span class="small">{{ feature }}</span>
                </li>
              </ul>
              
              <router-link 
                v-if="!plan.external"
                :to="plan.link"
                class="btn w-100"
                :class="plan.buttonClass">
                {{ plan.buttonText }}
              </router-link>
              
              <a 
                v-else
                :href="plan.link"
                class="btn w-100"
                :class="plan.buttonClass"
                target="_blank">
                {{ plan.buttonText }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { computed } from 'vue'
import { useTranslation } from '@/composables/useTranslation'

export default {
  name: 'PricingSection',
  setup() {
    const { t } = useTranslation()
    
    const plans = computed(() => [
      {
        title: t('pricing.essential.title'),
        subtitle: t('pricing.essential.subtitle'),
        price: t('pricing.essential.price'),
        currency: t('pricing.essential.currency'),
        period: t('pricing.essential.period'),
        cardClass: 'card-white',
        checkColor: 'text-success',
        buttonClass: 'btn-outline-dark',
        features: t('pricing.essential.features'),
        buttonText: t('pricing.essential.button'),
        link: '/inscription',
        external: false
      },
      {
        title: t('pricing.silver.title'),
        subtitle: t('pricing.silver.subtitle'),
        price: t('pricing.silver.price'),
        currency: t('pricing.silver.currency'),
        period: t('pricing.silver.period'),
        cardClass: 'card-silver',
        checkColor: 'text-white',
        buttonClass: 'btn-light',
        features: t('pricing.silver.features'),
        buttonText: t('pricing.silver.button'),
        link: '/inscription',
        external: false
      },
      {
        title: t('pricing.gold.title'),
        subtitle: t('pricing.gold.subtitle'),
        price: t('pricing.gold.price'),
        currency: t('pricing.gold.currency'),
        period: t('pricing.gold.period'),
        cardClass: 'card-gold',
        checkColor: 'text-warning',
        buttonClass: 'btn-dark',
        features: t('pricing.gold.features'),
        buttonText: t('pricing.gold.button'),
        link: '/inscription',
        external: false
      },
      {
        title: t('pricing.enterprise.title'),
        subtitle: t('pricing.enterprise.subtitle'),
        price: t('pricing.enterprise.price'),
        currency: '',
        period: '',
        cardClass: 'card-enterprise',
        checkColor: 'text-primary',
        buttonClass: 'btn-primary',
        features: t('pricing.enterprise.features'),
        buttonText: t('pricing.enterprise.button'),
        link: 'https://wa.me/25377604069?text=Nous%20voulons%20la%20solution%20enterprise%20pour%20notre%20équipe',
        external: true
      }
    ])
    
    return {
      t,
      plans
    }
  },
  mounted() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.fade-in').forEach(el => observer.observe(el));
  }
}
</script>

<style lang="scss" scoped>
.pricing-section {
  position: relative;
  overflow: hidden;
}

.pricing-card {
  border-radius: 20px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }

  // Card Essentiel - Blanc
  &.card-white {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 2px solid #e9ecef;

    .card-title {
      color: #212529;
    }
  }

  // Card Silver
  &.card-silver {
    background: linear-gradient(135deg, #8e9eab 0%, #c5cfd6 50%, #8e9eab 100%);
    border: 2px solid #a8b5bf;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        45deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      transform: rotate(45deg);
      animation: shimmer 3s infinite;
    }

    .card-title,
    .card-subtitle,
    .display-6,
    small,
    span {
      color: #ffffff;
      position: relative;
      z-index: 1;
    }

    .card-subtitle {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }

  // Card Gold
  &.card-gold {
    background: linear-gradient(135deg, #f7971e 0%, #ffd200 50%, #f7971e 100%);
    border: 2px solid #f7971e;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        45deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
      );
      transform: rotate(45deg);
      animation: shimmer 3s infinite;
    }

    .card-title {
      color: #1a1a1a;
      font-weight: 700;
      position: relative;
      z-index: 1;
    }

    .card-subtitle,
    .display-6,
    small,
    span {
      color: #1a1a1a;
      position: relative;
      z-index: 1;
    }

    .card-subtitle {
      color: rgba(26, 26, 26, 0.8) !important;
    }
  }

  // Card Entreprise
  &.card-enterprise {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: 2px solid #667eea;

    .card-title,
    .card-subtitle,
    .display-6,
    small,
    span {
      color: #ffffff;
    }

    .card-subtitle {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  }

  .card-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .price-container {
    .display-6 {
      font-weight: 800;
      font-size: 2.5rem;
    }
  }
}

// Animation shimmer pour Silver et Gold
@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

// Animation fade-in
.fade-in {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

// Délai d'animation pour chaque carte
.col-lg-3:nth-child(1) .fade-in {
  transition-delay: 0.1s;
}
.col-lg-3:nth-child(2) .fade-in {
  transition-delay: 0.2s;
}
.col-lg-3:nth-child(3) .fade-in {
  transition-delay: 0.3s;
}
.col-lg-3:nth-child(4) .fade-in {
  transition-delay: 0.4s;
}

// Responsive
@media (max-width: 991.98px) {
  .pricing-card {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 767.98px) {
  .pricing-section {
    padding: 3rem 0;
  }

  .card-body {
    padding: 2rem !important;
  }

  .price-container .display-6 {
    font-size: 2rem !important;
  }
}

@import '@/assets/styles/main.scss';
</style>